<template>
  <div class="rules-page">
    <section class="page-header">
      <div>
        <p class="kicker">Administracao</p>
        <h1>Regras de Relatorios - IA</h1>
      </div>
      <div class="header-actions">
        <v-btn outlined color="primary" @click="$router.push({ name: 'analise-relatorios-ia.validacao' })">
          Voltar
        </v-btn>
        <v-btn outlined color="secondary" :loading="loadingHistorico" @click="atualizarHistorico">
          <v-icon left small>mdi-brain</v-icon>
          Atualizar historico
        </v-btn>
        <v-btn color="primary" @click="novaRegra">
          <v-icon left small>mdi-plus</v-icon>
          Nova regra
        </v-btn>
      </div>
    </section>

    <v-card outlined class="filter-panel">
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="2">
            <v-select v-model="filtros.uf" :items="ufs" label="UF" outlined dense />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="filtros.tipo" :items="tiposFiltro" label="Tipo" outlined dense clearable />
          </v-col>
          <v-col cols="12" md="2" class="filter-actions">
            <v-btn color="primary" block :loading="loading" @click="carregarRegras">Filtrar</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="regrasFiltradas"
      :loading="loading"
      class="elevation-0"
      :items-per-page="15"
      no-data-text="Nenhuma regra encontrada."
    >
      <template v-slot:item.tipo="{ item }">
        <v-chip small dark :color="tipoColor(item.tipo)">{{ tipoLabel(item.tipo) }}</v-chip>
      </template>
      <template v-slot:item.textoRegra="{ item }">
        <div class="rule-text">{{ item.textoRegra }}</div>
      </template>
      <template v-slot:item.historico="{ item }">
        <div>{{ item.totalOcorrencias || 0 }} ocorr.; peso {{ formatNumber(item.pesoHistorico) }}</div>
        <small class="muted">ult. {{ formatDate(item.ultimaOcorrencia) }}</small>
      </template>
      <template v-slot:item.ativo="{ item }">
        <v-icon :color="item.ativo ? 'green' : 'grey'">{{ item.ativo ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
      </template>
      <template v-slot:item.acoes="{ item }">
        <v-btn icon small title="Editar" @click="editarRegra(item)">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon small title="Desativar" :disabled="!item.ativo" @click="desativarRegra(item)">
          <v-icon small color="red">mdi-delete-outline</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="820">
      <v-card>
        <v-card-title>{{ form.analiseRelatoriosIaRegraId ? "Editar regra" : "Nova regra" }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="2">
              <v-select v-model="form.uf" :items="ufs" label="UF" outlined dense />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="form.tipo" :items="tiposForm" label="Tipo" outlined dense />
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field v-model.number="form.prioridade" label="Prioridade" type="number" outlined dense />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="form.origem" label="Origem" outlined dense disabled />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="form.titulo" label="Titulo" outlined dense />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="form.textoRegra" label="Regra" outlined rows="5" auto-grow />
            </v-col>
            <v-col cols="12" md="4">
              <v-switch v-model="form.ativo" label="Ativa" inset />
            </v-col>
            <v-col cols="12" md="8" class="history-summary">
              <span>{{ form.totalOcorrencias || 0 }} ocorrencias historicas</span>
              <span>{{ form.totalAprovacoesHumanas || 0 }} aprovacoes</span>
              <span>{{ form.totalRejeicoesHumanas || 0 }} rejeicoes</span>
              <span>peso {{ formatNumber(form.pesoHistorico) }}</span>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="salvarRegra">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { atualUfDefinition } from "../../../helpers/utils";
import { analiseRelatoriosIaApi } from "../services/api";

function resolveUfAtual() {
  if (atualUfDefinition && atualUfDefinition.uf) return String(atualUfDefinition.uf).toUpperCase();
  return "SC";
}

function emptyForm() {
  return {
    analiseRelatoriosIaRegraId: null,
    uf: resolveUfAtual(),
    tipo: "MANUAL_REJEICAO",
    titulo: "",
    textoRegra: "",
    origem: "manual",
    prioridade: 100,
    ativo: true,
    totalOcorrencias: 0,
    totalAprovacoesHumanas: 0,
    totalRejeicoesHumanas: 0,
    pesoHistorico: 0,
    ultimaOcorrencia: null,
  };
}

export default {
  name: "AnaliseRelatoriosIaRegras",
  data() {
    return {
      loading: false,
      saving: false,
      loadingHistorico: false,
      dialog: false,
      regras: [],
      filtros: {
        uf: resolveUfAtual(),
        tipo: null,
      },
      form: emptyForm(),
      ufs: ["SC", "RJ", "SP", "MG", "MT", "MS", "AL", "MA", "CE", "BA", "PE", "PR", "RS"],
      tiposForm: [
        { text: "Aprovacao manual", value: "MANUAL_APROVACAO" },
        { text: "Rejeicao manual", value: "MANUAL_REJEICAO" },
        { text: "Aprovacao historica", value: "CRITERIO_APROVACAO_UF" },
        { text: "Rejeicao historica", value: "CRITERIO_REJEICAO_UF" },
        { text: "Rejeicao corrigida", value: "REJEICAO_HISTORICA_CORRIGIDA" },
        { text: "Tolerancia da UF", value: "TOLERANCIA_UF" },
      ],
      headers: [
        { text: "Tipo", value: "tipo", width: 190 },
        { text: "Titulo", value: "titulo", width: 220 },
        { text: "Regra", value: "textoRegra" },
        { text: "Historico", value: "historico", width: 210 },
        { text: "Ativa", value: "ativo", width: 80 },
        { text: "", value: "acoes", sortable: false, align: "end", width: 90 },
      ],
    };
  },
  computed: {
    tiposFiltro() {
      return this.tiposForm;
    },
    regrasFiltradas() {
      if (!this.filtros.tipo) return this.regras;
      return this.regras.filter((x) => x.tipo === this.filtros.tipo);
    },
  },
  async mounted() {
    await this.carregarRegras();
  },
  methods: {
    async carregarRegras() {
      this.loading = true;
      try {
        const data = await analiseRelatoriosIaApi.listarRegras({ uf: this.filtros.uf });
        this.regras = data.regras || [];
      } finally {
        this.loading = false;
      }
    },
    novaRegra() {
      this.form = emptyForm();
      this.form.uf = this.filtros.uf || resolveUfAtual();
      this.dialog = true;
    },
    editarRegra(item) {
      this.form = { ...emptyForm(), ...item, ativo: item.ativo !== false };
      this.dialog = true;
    },
    async salvarRegra() {
      this.saving = true;
      try {
        await analiseRelatoriosIaApi.salvarRegra(this.form);
        this.dialog = false;
        await this.carregarRegras();
      } finally {
        this.saving = false;
      }
    },
    async desativarRegra(item) {
      await analiseRelatoriosIaApi.desativarRegra(item.analiseRelatoriosIaRegraId);
      await this.carregarRegras();
    },
    async atualizarHistorico() {
      this.loadingHistorico = true;
      try {
        const resultado = await analiseRelatoriosIaApi.atualizarRegrasHistorico({ uf: this.filtros.uf });
        await this.carregarRegras();
        this.$notification.success(`${resultado.totalRegrasGeradas} regra(s) atualizada(s) pelo historico.`);
      } finally {
        this.loadingHistorico = false;
      }
    },
    tipoLabel(tipo) {
      const found = this.tiposForm.find((x) => x.value === tipo);
      return found ? found.text : tipo || "-";
    },
    tipoColor(tipo) {
      if (tipo === "MANUAL_APROVACAO" || tipo === "CRITERIO_APROVACAO_UF" || tipo === "TOLERANCIA_UF") return "green";
      if (tipo === "MANUAL_REJEICAO" || tipo === "CRITERIO_REJEICAO_UF") return "red";
      if (tipo === "REJEICAO_HISTORICA_CORRIGIDA") return "orange";
      return "grey";
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "0";
      return Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 });
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("pt-BR");
    },
  },
};
</script>

<style scoped>
.rules-page {
  padding: 24px 0;
}
.page-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.kicker {
  margin: 0 0 4px;
  font-size: 12px;
  text-transform: uppercase;
  color: #546e7a;
}
.page-header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
}
.header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}
.filter-panel {
  margin-bottom: 16px;
}
.filter-actions {
  display: flex;
  align-items: flex-start;
}
.rule-text {
  max-width: 620px;
  white-space: normal;
}
.muted {
  color: #607d8b;
}
.history-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  color: #546e7a;
}
@media (max-width: 960px) {
  .page-header {
    display: block;
  }
  .header-actions {
    justify-content: flex-start;
    margin-top: 12px;
  }
}
</style>
